import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { usePermissions } from '@/lib/roles';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { CustomSelect } from '@/components/ui/custom-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, ChevronRight, ChevronDown, Filter, ExternalLink, Layers, List, Scan, AlertTriangle, CheckCircle, XCircle, Loader2, Wand2, Trash2, GripVertical } from 'lucide-react';
import { ReadinessBadge } from '@/components/StoryReadinessPanel';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const stateColors = {
  CAPTURED: 'bg-[#f0dcf0] text-[#4F0077] dark:bg-[#98139C]/20 dark:text-[#d87bdb]', 
  INTAKE_COMPLETE: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-400', 
  REFINED: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400', 
  SIZED: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400', 
  PLANNED: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400', 
  IN_PROGRESS: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400', 
  DONE: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400', 
  DISCARDED: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400', 
  NEEDS_INFO: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400', 
  NEEDS_REWORK: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400', 
  DEFERRED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400', 
  BLOCKED: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
};

const urgencyColors = { 
  P0: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', 
  P1: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', 
  P2: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  P3: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  P4: 'bg-gray-50 text-gray-500 dark:bg-gray-900 dark:text-gray-500'
};

// Get item type from item data
const getItemType = (item) => {
  // Detect user stories (from user_stories collection — have as_a or sprint_id but no initiative_type)
  if (item.as_a !== undefined || (item.sprint_id !== undefined && !item.initiative_type)) {
    return 'user-story';
  }
  if (item.initiative_type === 'epic' || item.tags?.includes('initiative') || item.tags?.includes('epic')) {
    return 'epic';
  } else if (item.ado_type) {
    const adoType = item.ado_type.toLowerCase().replace(' ', '-');
    return adoType;
  } else if (item.tags) {
    return item.tags.find(t => ['user-story', 'feature', 'epic', 'task', 'bug'].includes(t)) || 'feature';
  }
  return 'feature';
};

// Get display name for type. Yggdrasil titles distinguish Capabilities
// (C-NN) from Foundations (F-NN). Both arrive in Speedforce as Epic but
// the display name surfaces the distinction so the backlog isn't a wall
// of identical "Initiative" labels.
const getTypeDisplay = (type, item) => {
  if (type === 'epic' && item?.title) {
    if (/^\s*C-\d{2}\b/i.test(item.title)) return 'Capability';
    if (/^\s*F-\d{2}\b/i.test(item.title)) return 'Foundation';
    return 'Initiative';
  }
  const displays = {
    'epic': 'Initiative',
    'feature': 'Feature',
    'user-story': 'Story',
    'story': 'Story',
    'task': 'Task',
    'bug': 'Bug'
  };
  return displays[type] || type;
};

// Resolve the parent "group" key for a hierarchy node so dnd-kit can
// restrict drops to within the same sibling list. Top-level epics share
// the synthetic 'root' group; features share the epic id; stories share
// the feature id.
const groupKeyFor = (node, parentId) => {
  if (node.type === 'epic') return 'root';
  return parentId || 'root';
};


// A row wrapper that registers with dnd-kit's useSortable. It exposes a
// drag handle (the grip icon) so clicking elsewhere on the row still
// opens the detail modal. The outer div carries the transform style
// the sortable layout needs.
function SortableRow({ id, groupKey, kind, depth, children, isDragOverlay }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, data: { groupKey, kind, depth } });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    background: isDragging ? 'rgba(152,19,156,0.04)' : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} data-sortable-id={id}>
      {typeof children === 'function' ? children({ dragHandle: { attributes, listeners } }) : children}
    </div>
  );
}


export default function Backlog() {
  const can = usePermissions();
  const canEdit = can('editWork');
  const canAi = can('runAi');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [stories, setStories] = useState([]);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState(searchParams.get('state') || 'all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('hierarchy'); // 'hierarchy', 'flat', or 'tasks'
  const [expandedItems, setExpandedItems] = useState(new Set());

  // ADO Tasks tab state
  const [adoTasks, setAdoTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  
  // Quality Scout state
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [applyingFix, setApplyingFix] = useState(null);
  const [qualityIssues, setQualityIssues] = useState({}); // Map of item_id to quality data
  const [scanProgress, setScanProgress] = useState(null); // { processed_count, total_items }
  const scanPollRef = useRef(null);
  const activeScanIdRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        // Load all intake items (filtering done client-side for product+state combo)
        const res = await api.get('/intake/items');
        setItems(res.data);
        
        // Load all stories for hierarchy view
        const storiesRes = await api.get('/sprints/stories');
        setStories(storiesRes.data);

        // Load ADO work items (tasks/bugs) for hierarchy nesting
        try {
          const adoRes = await api.get('/devops/work-items');
          setAdoTasks(adoRes.data);
        } catch { /* ADO work items are optional */ }

        // Load products for filter
        const prodRes = await api.get('/products/');
        setProducts(prodRes.data);
      } catch (err) { 
        console.error(err);
      } finally { 
        setLoading(false); 
      }
    };
    load();
  }, []);

  // Load ADO tasks when switching to tasks tab
  useEffect(() => {
    if (viewMode === 'tasks') {
      setLoadingTasks(true);
      const params = {};
      if (productFilter !== 'all') params.product_id = productFilter;
      api.get('/devops/work-items', { params })
        .then(res => setAdoTasks(res.data))
        .catch(err => console.error('Failed to load ADO tasks:', err))
        .finally(() => setLoadingTasks(false));
    }
  }, [viewMode, productFilter]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (scanPollRef.current) {
        clearInterval(scanPollRef.current);
      }
    };
  }, []);

  // Quality Scout - Scan for issues (async with polling)
  const scanForIssues = async () => {
    if (!productFilter || productFilter === 'all') {
      alert('Please select a product to scan');
      return;
    }

    const selectedProduct = products.find(p => p.name === productFilter);
    if (!selectedProduct) return;

    setScanning(true);
    setScanResults(null);
    setScanProgress(null);

    try {
      // Start the scan (returns immediately with scan_id)
      const res = await api.post(`/ai/quality-scout?product_id=${selectedProduct.id}`);
      const { scan_id, total_items } = res.data;

      activeScanIdRef.current = scan_id;
      setScanProgress({ processed_count: 0, total_items });
      setShowScanModal(true);

      // Poll for results every 3 seconds
      scanPollRef.current = setInterval(async () => {
        try {
          const pollRes = await api.get(`/ai/quality-scout/scans/${scan_id}`);
          const scanData = pollRes.data;

          if (scanData.status === 'completed') {
            clearInterval(scanPollRef.current);
            scanPollRef.current = null;
            activeScanIdRef.current = null;

            setScanResults({
              success: true,
              scan_id: scanData.id,
              issues: scanData.issues,
              summary: {
                total_scanned: scanData.summary?.total || 0,
                critical_gaps: scanData.summary?.critical_gaps || 0,
                needs_work: scanData.summary?.needs_work || 0,
                complete: scanData.summary?.complete || 0,
                average_quality: scanData.summary?.average_quality || 0
              }
            });

            const issuesMap = {};
            scanData.issues?.forEach(issue => {
              issuesMap[issue.item_id] = issue;
            });
            setQualityIssues(issuesMap);
            setScanning(false);
            setScanProgress(null);

          } else if (scanData.status === 'failed') {
            clearInterval(scanPollRef.current);
            scanPollRef.current = null;
            activeScanIdRef.current = null;

            setScanning(false);
            setScanProgress(null);
            alert('Scan failed: ' + (scanData.error || 'Unknown error'));

          } else {
            // Still processing - update progress
            setScanProgress({
              processed_count: scanData.processed_count || 0,
              total_items: scanData.total_items || total_items
            });
          }
        } catch (pollErr) {
          console.error('Poll failed:', pollErr);
        }
      }, 3000);

    } catch (err) {
      console.error('Scan start failed:', err);
      alert('Failed to start scan. Please try again.');
      setScanning(false);
      setScanProgress(null);
    }
  };

  // Apply a single improvement
  const applyImprovement = async (itemId, improvements) => {
    setApplyingFix(itemId);
    try {
      await api.post(`/ai/quality-scout/apply/${itemId}`, improvements);
      
      // Update local state to reflect fix
      setQualityIssues(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      
      // Update scan results
      setScanResults(prev => ({
        ...prev,
        issues: prev.issues.filter(i => i.item_id !== itemId)
      }));
      
    } catch (err) {
      console.error('Apply fix failed:', err);
      alert('Failed to apply improvement');
    } finally {
      setApplyingFix(null);
    }
  };

  // Apply all improvements for an item
  const applyAllImprovements = async (issue) => {
    const improvements = issue.proposed_improvements?.map(imp => ({
      field: imp.field,
      proposed: imp.proposed
    })) || [];
    
    if (improvements.length > 0) {
      await applyImprovement(issue.item_id, improvements);
    }
  };

  // Delete an intake item (also removes from ADO if linked)
  const deleteItem = async (item) => {
    if (!item?.id) return;
    const confirmed = window.confirm(
      `Delete "${item.title}"? This also removes it from Azure DevOps.`
    );
    if (!confirmed) return;

    try {
      await api.delete(`/intake/items/${item.id}`);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setQualityIssues(prev => {
        if (!prev[item.id]) return prev;
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    } catch (err) {
      console.error('Delete failed:', err);
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      alert(`Failed to delete item: ${msg}`);
    }
  };

  // Story-level states: when filtering by these, also show parent intake items
  const storyStates = new Set(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'BLOCKED']);

  // Apply all filters
  const filteredItems = useMemo(() => {
    // When filtering by a story-relevant state, find intake items that have matching child stories
    const intakeIdsWithMatchingStories = new Set();
    if (stateFilter && stateFilter !== 'all') {
      const targetStates = stateFilter.includes(',') ? stateFilter.split(',') : [stateFilter];
      const hasStoryState = targetStates.some(s => storyStates.has(s));
      if (hasStoryState) {
        stories.forEach(story => {
          if (targetStates.includes(story.state) && story.intake_id) {
            intakeIdsWithMatchingStories.add(story.intake_id);
            // Also add the parent epic so hierarchy isn't broken
            const parent = items.find(i => i.id === story.intake_id);
            if (parent?.parent_id) intakeIdsWithMatchingStories.add(parent.parent_id);
          }
        });
      }
    }

    return items.filter(item => {
      // Pre-approval gate: CAPTURED is the state assigned at intake submit,
      // before the G1 approval flips the item to INTAKE_COMPLETE. We don't
      // want unapproved requests appearing in the backlog. Honor an explicit
      // CAPTURED filter though, in case a PM wants to see the queue.
      if (item.state === 'CAPTURED' && stateFilter !== 'CAPTURED') {
        return false;
      }

      // Search filter
      const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase()) ||
                            (item.product || '').toLowerCase().includes(search.toLowerCase());

      // Type filter
      const itemType = getItemType(item);
      const matchesType = typeFilter === 'all' || itemType === typeFilter;

      // Product filter
      const matchesProduct = productFilter === 'all' || item.product === productFilter;

      // State filter (handle multiple states separated by comma)
      let matchesState = true;
      if (stateFilter && stateFilter !== 'all') {
        if (stateFilter.includes(',')) {
          const allowedStates = stateFilter.split(',');
          matchesState = allowedStates.includes(item.state);
        } else {
          matchesState = item.state === stateFilter;
        }
        // Also include intake items that have child stories matching the state
        if (!matchesState && intakeIdsWithMatchingStories.has(item.id)) {
          matchesState = true;
        }
      }

      return matchesSearch && matchesType && matchesProduct && matchesState;
    });
  }, [items, stories, search, typeFilter, productFilter, stateFilter]);

  // Filter stories separately
  const filteredStories = useMemo(() => {
    return stories.filter(story => {
      // Search filter
      const matchesSearch = story.title.toLowerCase().includes(search.toLowerCase());
      
      // Product filter - stories have product_id
      const matchesProduct = productFilter === 'all' || 
        story.product_id === products.find(p => p.name === productFilter)?.id ||
        story.product === productFilter;
      
      // State filter for stories
      let matchesState = true;
      if (stateFilter && stateFilter !== 'all') {
        if (stateFilter.includes(',')) {
          const allowedStates = stateFilter.split(',');
          matchesState = allowedStates.includes(story.state);
        } else {
          matchesState = story.state === stateFilter;
        }
      }
      
      return matchesSearch && matchesProduct && matchesState;
    });
  }, [stories, search, productFilter, stateFilter, products]);

  // Pre-compute total RICE scores for features and epics (used in flat view)
  const totalRiceMap = useMemo(() => {
    const map = {};
    // Features: sum of their stories' RICE scores
    items.forEach(item => {
      const type = getItemType(item);
      if (type === 'feature') {
        const childStories = stories.filter(s => s.intake_id === item.id && s.rice_score);
        if (childStories.length > 0) {
          map[item.id] = Math.round(childStories.reduce((sum, s) => sum + s.rice_score, 0) * 10) / 10;
        }
      }
    });
    // Epics: sum of children (feature totals + direct stories)
    items.forEach(item => {
      const type = getItemType(item);
      if (type === 'epic') {
        const childFeatures = items.filter(f => f.parent_id === item.id);
        const directStories = stories.filter(s => s.intake_id === item.id && s.rice_score);
        let total = 0;
        let hasScores = false;
        childFeatures.forEach(f => { if (map[f.id]) { total += map[f.id]; hasScores = true; } });
        directStories.forEach(s => { total += s.rice_score; hasScores = true; });
        if (hasScores) {
          map[item.id] = Math.round(total * 10) / 10;
        }
      }
    });
    return map;
  }, [items, stories]);

  // Build hierarchy structure
  const hierarchy = useMemo(() => {
    // Group items by type
    const epics = filteredItems.filter(item => getItemType(item) === 'epic');
    const features = filteredItems.filter(item => getItemType(item) === 'feature');

    const tree = [];

    // Helper: compute total RICE score from an array of children
    const totalRice = (children) => {
      const scored = children.filter(c => c.rice_score || c.total_rice_score);
      if (scored.length === 0) return null;
      const sum = scored.reduce((acc, c) => acc + (c.rice_score || c.total_rice_score || 0), 0);
      return Math.round(sum * 10) / 10;
    };

    // Helper: walk a node's subtree and collect every story-level descendant
    // so we can roll an aggregate state up to parent Features and Epics.
    const collectStories = (node) => {
      if (!node) return [];
      if (node.type === 'story') return [node];
      const out = [];
      for (const c of node.children || []) out.push(...collectStories(c));
      return out;
    };

    // Roll up the effective state from the children. "INTAKE COMPLETE" is
    // misleading on an Epic whose stories are already IN_PROGRESS or DONE,
    // so we replace it with the strongest active signal:
    //   any active or blocked     -> IN_PROGRESS
    //   any in review             -> IN_REVIEW
    //   all done (ignore discard) -> DONE
    //   all discarded             -> DISCARDED
    //   none of the above         -> keep the original state
    const rollupState = (node) => {
      const stories = collectStories(node);
      if (stories.length === 0) return node.state;
      const live = stories.filter(s => s.state !== 'DISCARDED');
      if (live.length === 0) return 'DISCARDED';
      if (live.some(s => s.state === 'IN_PROGRESS' || s.state === 'BLOCKED')) return 'IN_PROGRESS';
      if (live.some(s => s.state === 'IN_REVIEW')) return 'IN_REVIEW';
      if (live.every(s => s.state === 'DONE')) return 'DONE';
      return node.state;
    };

    // Map capability code (C-NN / F-NN) -> epic. Used to rescue orphan
    // features whose ADO parent link was never set but whose title carries
    // the capability prefix (e.g. "C-09 [F3] Guardrails ..." attaches to the
    // C-09 epic). Without this many features show at top level alongside
    // epics and the backlog looks flat.
    const CAP_RE = /\b([CF]-\d{2})\b/i;
    const FEATURE_RE = /\b([CF]-\d{2})\s*\[F(\d+)\]/i;
    const epicByCap = new Map();
    for (const e of epics) {
      const m = CAP_RE.exec(e.title || '');
      if (m) epicByCap.set(m[1].toUpperCase(), e);
    }
    // Map "C-NN [F#]" -> the feature that owns that slot, so orphan
    // stories with a matching prefix get re-parented to their proper
    // feature instead of dumping into "Standalone Stories".
    const featureBySlot = new Map();
    for (const f of features) {
      const m = FEATURE_RE.exec(f.title || '');
      if (m) {
        const key = `${m[1].toUpperCase()}-F${m[2]}`;
        if (!featureBySlot.has(key)) featureBySlot.set(key, f);
      }
    }
    const findEpicForFeature = (feature) => {
      if (feature.parent_id) {
        const direct = epics.find(e => e.id === feature.parent_id);
        if (direct) return direct;
      }
      const m = CAP_RE.exec(feature.title || '');
      if (!m) return null;
      return epicByCap.get(m[1].toUpperCase()) || null;
    };
    const findFeatureForStory = (story) => {
      if (story.intake_id) {
        const direct = features.find(f => f.id === story.intake_id);
        // If story already points to a real feature (not the orphan
        // container), use that.
        if (direct && !(direct.tags || []).includes('orphan-stories')) return direct;
      }
      const m = FEATURE_RE.exec(story.title || '');
      if (!m) return null;
      const key = `${m[1].toUpperCase()}-F${m[2]}`;
      return featureBySlot.get(key) || null;
    };

    epics.forEach(epic => {
      const epicNode = {
        ...epic,
        type: 'epic',
        children: []
      };

      // Features that belong to this epic, either by explicit parent_id OR
      // by capability-code rescue (C-NN/F-NN match on title).
      const childFeatures = features.filter(f => findEpicForFeature(f)?.id === epic.id);
      childFeatures.forEach(feature => {
        const featureNode = {
          ...feature,
          type: 'feature',
          children: []
        };

        // Stories that belong to this feature: direct match on intake_id
        // PLUS orphan stories whose title resolves to this feature via the
        // C-NN [F#] prefix rescue.
        const childStories = filteredStories.filter(s => findFeatureForStory(s)?.id === feature.id);
        featureNode.children = childStories.map(s => {
          // Attach ADO tasks/bugs as children of stories
          const storyTasks = adoTasks.filter(t => t.parent_story_id === s.id || (t.parent_id && (s.ado_story_id || s.ado_id) && t.parent_id === (s.ado_story_id || s.ado_id)));
          return { ...s, type: 'story', children: storyTasks.map(t => ({ ...t, type: t.type === 'Bug' ? 'bug' : 'task' })) };
        });

        featureNode.total_rice_score = totalRice(featureNode.children);
        // Bubble story state up so a feature with IN_PROGRESS stories
        // stops reading as INTAKE_COMPLETE.
        featureNode.state = rollupState(featureNode);

        epicNode.children.push(featureNode);
      });

      // Also add stories directly under epic if no feature (use filteredStories)
      const directStories = filteredStories.filter(s => s.intake_id === epic.id);
      directStories.forEach(s => {
        const storyTasks = adoTasks.filter(t => t.parent_story_id === s.id || (t.parent_id && (s.ado_story_id || s.ado_id) && t.parent_id === (s.ado_story_id || s.ado_id)));
        epicNode.children.push({ ...s, type: 'story', children: storyTasks.map(t => ({ ...t, type: t.type === 'Bug' ? 'bug' : 'task' })) });
      });

      epicNode.total_rice_score = totalRice(epicNode.children);
      // Bubble state up from all descendants (features + stories).
      epicNode.state = rollupState(epicNode);

      tree.push(epicNode);
    });

    // Standalone features: have no parent epic via parent_id and the
    // capability-code rescue above also failed. Synthetic "Standalone
    // Stories - <product>" orphan containers from the import are hidden
    // when they have zero child stories so they don't pollute the list.
    const standaloneFeatures = features.filter(f => !findEpicForFeature(f));
    standaloneFeatures.forEach(feature => {
      const featureNode = {
        ...feature,
        type: 'feature',
        children: []
      };

      // Use the same story rescue so the orphan "Standalone Stories"
      // container only shows stories that genuinely don't match a feature.
      const childStories = filteredStories.filter(s => findFeatureForStory(s)?.id === feature.id);
      featureNode.children = childStories.map(s => {
        const storyTasks = adoTasks.filter(t => t.parent_story_id === s.id || (t.parent_id && (s.ado_story_id || s.ado_id) && t.parent_id === (s.ado_story_id || s.ado_id)));
        return { ...s, type: 'story', children: storyTasks.map(t => ({ ...t, type: t.type === 'Bug' ? 'bug' : 'task' })) };
      });
      featureNode.total_rice_score = totalRice(featureNode.children);
      featureNode.state = rollupState(featureNode);

      // Hide the auto-generated "Standalone Stories - <product>" container
      // when it has no children — it exists for orphan stories only and
      // adds visual noise otherwise.
      const isOrphanContainer = (feature.tags || []).includes('orphan-stories');
      if (isOrphanContainer && featureNode.children.length === 0) {
        return;
      }

      tree.push(featureNode);
    });
    
    // Show standalone stories that match filters but aren't nested under a visible parent
    if (typeFilter === 'all' || typeFilter === 'user-story') {
      const parentItemIds = new Set(filteredItems.map(i => i.id));
      const orphanStories = filteredStories.filter(s => !s.intake_id || !parentItemIds.has(s.intake_id));
      orphanStories.forEach(story => {
        const storyTasks = adoTasks.filter(t => t.parent_story_id === story.id || (t.parent_id && (story.ado_story_id || story.ado_id) && t.parent_id === (story.ado_story_id || story.ado_id)));
        tree.push({ ...story, type: 'story', children: storyTasks.map(t => ({ ...t, type: t.type === 'Bug' ? 'bug' : 'task' })) });
      });
    }
    
    return tree;
  }, [filteredItems, filteredStories, stateFilter, typeFilter, adoTasks]);

  // Default view is fully collapsed — the user explicitly opens what they
  // want to see via the chevron or the Expand All button.

  const toggleExpand = (id) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // --- Drag-and-drop reordering ------------------------------------------
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Apply optimistic local reorder + persist to backend. `kind` selects
  // which collection (intake_items vs user_stories) and which API endpoint.
  const persistOrder = useCallback(async (kind, orderedIds) => {
    if (!orderedIds || orderedIds.length < 2) return;
    const payload = orderedIds.map((id, idx) => ({ id, sort_order: idx * 100 }));
    try {
      if (kind === 'story') {
        await api.post('/sprints/stories/reorder', { stories: payload });
      } else {
        await api.post('/intake/items/reorder', { items: payload });
      }
    } catch (err) {
      console.error('Reorder persist failed:', err);
    }
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;
    const ag = active.data.current?.groupKey;
    const og = over.data.current?.groupKey;
    if (ag !== og) return;  // disallow cross-parent moves for now
    const kind = active.data.current?.kind;

    // Mutate intake items or stories based on kind, keeping the rest of the
    // collection untouched. The hierarchy useMemo recomputes from these
    // arrays, so changing the underlying list order is sufficient.
    if (kind === 'story') {
      setStories(prev => {
        const oldIndex = prev.findIndex(s => s.id === active.id);
        const newIndex = prev.findIndex(s => s.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return prev;
        const reordered = arrayMove(prev, oldIndex, newIndex);
        // Persist using the slice of stories that belong to this parent
        // (groupKey === feature id, or 'root' for top-level orphan stories).
        const siblingIds = reordered.filter(s => (s.intake_id || 'root') === ag).map(s => s.id);
        persistOrder('story', siblingIds);
        // Also stamp sort_order locally so the next hierarchy rebuild
        // respects the new order without a reload.
        return reordered.map((s, i) => ({ ...s, sort_order: s.id ? (siblingIds.indexOf(s.id) >= 0 ? siblingIds.indexOf(s.id) * 100 : s.sort_order) : s.sort_order }));
      });
    } else {
      // Epic or feature reorder
      setItems(prev => {
        const oldIndex = prev.findIndex(i => i.id === active.id);
        const newIndex = prev.findIndex(i => i.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return prev;
        const reordered = arrayMove(prev, oldIndex, newIndex);
        // Siblings: same parent_id (or undefined for top-level epics).
        const parentId = ag === 'root' ? null : ag;
        const siblingIds = reordered
          .filter(i => (parentId == null ? !i.parent_id : i.parent_id === parentId))
          .map(i => i.id);
        persistOrder(kind || 'intake', siblingIds);
        return reordered.map(i => {
          const idx = siblingIds.indexOf(i.id);
          return idx >= 0 ? { ...i, sort_order: idx * 100 } : i;
        });
      });
    }
  }, [persistOrder]);
  // -----------------------------------------------------------------------

  const expandAll = () => {
    const allIds = new Set();
    hierarchy.forEach(item => {
      allIds.add(item.id);
      item.children?.forEach(child => {
        allIds.add(child.id);
      });
    });
    setExpandedItems(allIds);
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  const states = ['all', 'CAPTURED', 'INTAKE_COMPLETE', 'REFINED', 'SIZED', 'PLANNED', 'TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'BLOCKED'];

  // Render a single row
  const renderRow = (item, depth = 0, type = 'feature', parentId = null) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const itemType = type || getItemType(item);
    const displayType = getTypeDisplay(itemType, item);
    const groupKey = groupKeyFor(item, parentId);
    // kind drives which collection the reorder endpoint hits.
    const sortableKind = itemType === 'story' || itemType === 'user-story' ? 'story' : 'intake';
    // Tasks and bugs aren't draggable (read-only from ADO).
    // Reordering needs pm (route matrix); others get a static row.
    const draggable = canEdit && sortableKind !== undefined && itemType !== 'task' && itemType !== 'bug';

    const RowInner = ({ dragHandle }) => (
      <div key={item.id}>
        <div
          data-testid={`backlog-row-${item.id}`}
          onClick={() => {
            if (itemType === 'task' || itemType === 'bug') {
              // Tasks/bugs open in ADO
              if (item.ado_url) window.open(item.ado_url, '_blank');
            } else if (itemType === 'story') {
              // Stories open inline in their parent feature's detail view
              // via the ?story={id} query param so the modal auto-opens.
              if (item.intake_id) {
                navigate(`/backlog/${item.intake_id}?story=${item.id}`);
              } else {
                navigate(`/backlog/${item.id}`);
              }
            } else {
              navigate(`/backlog/${item.id}`);
            }
          }}
          className={`flex items-center border-b border-[#e3e3e3] dark:border-white/10 hover:bg-[#faf8fb] dark:hover:bg-white/5 transition-colors cursor-pointer ${depth > 0 ? 'bg-[#fafafa] dark:bg-white/[0.02]' : ''}`}
          style={{ paddingLeft: `${16 + depth * 24}px` }}
        >
          {/* Drag handle — only this element activates the sortable drag,
              so clicking elsewhere on the row still opens the detail modal. */}
          {draggable && dragHandle ? (
            <button
              {...dragHandle.attributes}
              {...dragHandle.listeners}
              onClick={(e) => e.stopPropagation()}
              className="w-5 flex-shrink-0 flex items-center justify-center text-[#30302F]/30 dark:text-white/30 hover:text-[#98139C] cursor-grab active:cursor-grabbing"
              title="Drag to reorder"
              aria-label="Drag to reorder"
            >
              <GripVertical size={14} />
            </button>
          ) : (
            <span className="w-5 flex-shrink-0" />
          )}

          {/* Expand/Collapse button */}
          <div className="w-6 flex-shrink-0">
            {hasChildren ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(item.id);
                }}
                className="p-0.5 hover:bg-gray-200 dark:hover:bg-white/10 rounded"
              >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            ) : (
              <span className="w-4" />
            )}
          </div>
          
          {/* Title — min-w-0 on the inner flex lets the truncate take effect
              so long titles stop pushing the rest of the row to the right. */}
          <div className="flex-1 py-3 pr-4 min-w-[200px] overflow-hidden">
            <div className="flex items-center gap-2 min-w-0">
              {item.intake_ref && (
                <span className="shrink-0 text-xs text-[#30302F] dark:text-white font-mono">{item.intake_ref}</span>
              )}
              <span className="font-medium text-[#30302F] dark:text-white truncate min-w-0" title={item.title}>{item.title}</span>
              {/* Quality Issue Indicator */}
              {qualityIssues[item.id] && (
                <span
                  className={`shrink-0 flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded ${
                    qualityIssues[item.id].status === 'critical_gaps'
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                  }`}
                  title={`Quality Score: ${qualityIssues[item.id].quality_score}%`}
                >
                  <AlertTriangle size={10} />
                  {qualityIssues[item.id].missing_fields?.length || 0} gaps
                </span>
              )}
            </div>
          </div>
          
          {/* Type - plain text, no color */}
          <div className="w-24 py-3 px-2 text-xs text-[#30302F] dark:text-white">
            {displayType}
          </div>
          
          {/* Product (hidden < xl) */}
          <div className="w-32 py-3 px-2 text-[#30302F] dark:text-white font-light text-sm truncate hidden xl:block">
            {item.product || '—'}
          </div>

          {/* State */}
          <div className="w-28 py-3 px-2">
            <Badge className={`text-[11px] rounded-none ${stateColors[item.state] || stateColors[item.ado_state] || 'bg-gray-100 text-gray-500'}`}>
              {(item.state || item.ado_state || 'TODO')?.replace(/_/g, ' ')}
            </Badge>
          </div>

          {/* Priority */}
          <div className="w-20 py-3 px-2">
            {(item.urgency || item.priority) ? (
              <Badge className={`text-[11px] rounded-none ${
                (item.urgency || item.priority) === 'P0' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                (item.urgency || item.priority) === 'P1' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                (item.urgency || item.priority) === 'P2' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                (item.urgency || item.priority) === 'P3' ? 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-white/70' :
                (item.urgency || item.priority) === 'P4' ? 'bg-gray-50 text-gray-500 dark:bg-gray-900 dark:text-gray-500' :
                'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-white/70'
              }`}>
                {item.urgency || item.priority}
              </Badge>
            ) : (
              <span className="text-[#30302F]/40 dark:text-white/40 text-xs">—</span>
            )}
          </div>
          
          {/* RICE Score — stories show their own score, features/epics show avg (hidden < 2xl) */}
          <div className="w-20 py-3 px-2 hidden 2xl:block">
            {(() => {
              const score = item.rice_score || item.total_rice_score;
              const isTotal = !item.rice_score && item.total_rice_score;
              if (!score) return <span className="text-[#30302F]/40 dark:text-white/40 text-xs">—</span>;
              return (
                <span className={`text-xs font-medium ${
                  score >= 10 ? 'text-green-600 dark:text-green-400' :
                  score >= 5 ? 'text-amber-600 dark:text-amber-400' :
                  'text-[#30302F]/80 dark:text-white/80'
                }`} title={isTotal ? `Sum of ${item.children?.filter(c => c.rice_score || c.total_rice_score).length || 0} scored children` : undefined}>
                  {isTotal ? 'Σ ' : ''}{score.toFixed(1)}
                </span>
              );
            })()}
          </div>

          {/* Readiness (hidden < 2xl) */}
          <div className="w-24 py-3 px-2 hidden 2xl:block">
            {item.readiness ? <ReadinessBadge readiness={item.readiness} /> : <span className="text-[#30302F]/40 dark:text-white/40 text-xs">&mdash;</span>}
          </div>

          {/* ADO Link */}
          <div className="w-24 py-3 px-2">
            {item.ado_url ? (
              <a
                href={item.ado_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[#0078d4] hover:text-[#106ebe] text-xs"
                title="Open in Azure DevOps"
              >
                <ExternalLink size={12} />
                <span>#{item.ado_id}</span>
              </a>
            ) : (
              <span className="text-[#30302F] dark:text-white text-xs">&mdash;</span>
            )}
          </div>

          {/* Updated (hidden < lg) */}
          <div className="w-24 py-3 px-2 text-[#30302F] dark:text-white text-xs font-light hidden lg:block">
            {new Date(item.updated_at || item.created_at).toLocaleDateString()}
          </div>

          {/* Actions */}
          <div className="w-20 py-3 px-2 flex items-center justify-center">
            {canEdit && itemType !== 'story' && itemType !== 'user-story' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteItem(item);
                }}
                className="p-1 rounded text-[#30302F]/60 hover:text-red-600 hover:bg-red-50 dark:text-white/60 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                title="Delete item (also removes from Azure DevOps)"
                aria-label={`Delete ${item.title}`}
                data-testid={`backlog-delete-${item.id}`}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Render children if expanded — wrapped in a SortableContext so
            sibling reordering stays scoped to this parent. */}
        {hasChildren && isExpanded && (
          <div>
            <SortableContext
              items={item.children.map(c => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {item.children.map(child => renderRow(child, depth + 1, child.type, item.id))}
            </SortableContext>
          </div>
        )}
      </div>
    );

    // Non-draggable rows (tasks/bugs) skip the SortableRow wrapper.
    if (!draggable) {
      return <RowInner key={item.id} />;
    }
    return (
      <SortableRow
        key={item.id}
        id={item.id}
        groupKey={groupKey}
        kind={sortableKind}
        depth={depth}
      >
        {({ dragHandle }) => <RowInner dragHandle={dragHandle} />}
      </SortableRow>
    );
  };

  // Flat view row
  const renderFlatRow = (item) => {
    const itemType = getItemType(item);
    const displayType = getTypeDisplay(itemType, item);
    
    return (
      <tr 
        key={item.id} 
        data-testid={`backlog-row-${item.id}`}
        onClick={() => {
          const t = getItemType(item);
          if (t === 'user-story' && item.intake_id) {
            navigate(`/backlog/${item.intake_id}?story=${item.id}`);
          } else {
            navigate(`/backlog/${item.id}`);
          }
        }}
        className="border-b border-[#e3e3e3] dark:border-white/10 last:border-0 hover:bg-[#faf8fb] dark:hover:bg-white/5 transition-colors cursor-pointer"
      >
        <td className="p-4">
          {item.intake_ref && (
            <span className="mr-2 text-xs text-[#30302F] dark:text-white font-mono">{item.intake_ref}</span>
          )}
          <span className="font-medium text-[#30302F] dark:text-white">{item.title}</span>
        </td>
        <td className="p-4 text-xs text-[#30302F] dark:text-white">
          {displayType}
        </td>
        <td className="p-4 text-[#30302F] dark:text-white font-light">{item.product || '—'}</td>
        <td className="p-4">
          <Badge className={`text-[11px] rounded-none ${stateColors[item.state] || 'bg-gray-100 text-gray-500'}`}>
            {item.state?.replace(/_/g, ' ')}
          </Badge>
        </td>
        <td className="p-4">
          {(item.urgency || item.priority) ? (
            <Badge className={`text-[11px] rounded-none ${
              (item.urgency || item.priority) === 'P0' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
              (item.urgency || item.priority) === 'P1' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
              (item.urgency || item.priority) === 'P2' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
              (item.urgency || item.priority) === 'P3' ? 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-white/70' :
              (item.urgency || item.priority) === 'P4' ? 'bg-gray-50 text-gray-500 dark:bg-gray-900 dark:text-gray-500' :
              'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-white/70'
            }`}>
              {item.urgency || item.priority}
            </Badge>
          ) : (
            <span className="text-[#30302F]/40 dark:text-white/40 text-xs">—</span>
          )}
        </td>
        <td className="p-4">
          {(() => {
            const score = item.rice_score || totalRiceMap[item.id];
            const isTotal = !item.rice_score && totalRiceMap[item.id];
            if (!score) return <span className="text-[#30302F]/40 dark:text-white/40 text-xs">—</span>;
            return (
              <span className={`text-xs font-medium ${
                score >= 10 ? 'text-green-600 dark:text-green-400' :
                score >= 5 ? 'text-amber-600 dark:text-amber-400' :
                'text-[#30302F]/80 dark:text-white/80'
              }`} title={isTotal ? 'Sum of children' : undefined}>
                {isTotal ? 'Σ ' : ''}{score.toFixed(1)}
              </span>
            );
          })()}
        </td>
        <td className="p-4">
          {item.readiness ? <ReadinessBadge readiness={item.readiness} /> : <span className="text-[#30302F]/40 dark:text-white/40 text-xs">&mdash;</span>}
        </td>
        <td className="p-4">
          {item.ado_url ? (
            <a
              href={item.ado_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[#0078d4] hover:text-[#106ebe] text-xs"
              title="Open in Azure DevOps"
            >
              <ExternalLink size={12} />
              <span>#{item.ado_id}</span>
            </a>
          ) : (
            <span className="text-[#30302F] dark:text-white text-xs">&mdash;</span>
          )}
        </td>
        <td className="p-4 text-[#30302F] dark:text-white text-xs font-light">
          {new Date(item.updated_at).toLocaleDateString()}
        </td>
        <td className="p-4">
          {canEdit && itemType !== 'user-story' && itemType !== 'story' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteItem(item);
              }}
              className="p-1 rounded text-[#30302F]/60 hover:text-red-600 hover:bg-red-50 dark:text-white/60 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors"
              title="Delete item (also removes from Azure DevOps)"
              aria-label={`Delete ${item.title}`}
              data-testid={`backlog-delete-${item.id}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="p-4 lg:p-8" data-testid="backlog-page">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">Product</p>
        <h1 className="text-4xl font-heading text-[#30302F] dark:text-white mt-1" style={{ letterSpacing: '-0.8px' }}>Backlog</h1>
        <p className="text-[#30302F] dark:text-white mt-2 font-light text-lg">
          {filteredItems.length + filteredStories.length} items across all states
        </p>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#30302F] dark:text-white" />
          <Input
            data-testid="backlog-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items..."
            aria-label="Search backlog items"
            className="pl-9 rounded-none border-[#e3e3e3] dark:border-white/10 dark:bg-white/5 dark:text-white focus-visible:ring-[#98139C]"
          />
        </div>
        
        {/* Type Filter */}
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#30302F] dark:text-white" />
          <CustomSelect
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'user-story', label: 'User Story' },
              { value: 'feature', label: 'Feature' },
              { value: 'epic', label: 'Initiative (Epic)' },
              { value: 'task', label: 'Task' },
              { value: 'bug', label: 'Bug' },
            ]}
            data-testid="type-filter"
            aria-label="Filter by item type"
          />
        </div>
        
        {/* Product Filter */}
        <CustomSelect
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          options={[
            { value: 'all', label: 'All Products' },
            ...products.map(p => ({ value: p.name, label: p.name }))
          ]}
          data-testid="product-filter"
          aria-label="Filter by product"
        />
        
        {/* Quality Scout Button */}
        {canAi && (
        <button
          onClick={scanForIssues}
          disabled={scanning || productFilter === 'all'}
          className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
            productFilter === 'all'
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-white/5 dark:text-white/40'
              : 'bg-[#98139C] text-white hover:bg-[#7a0f7d]'
          }`}
          data-testid="scan-quality-btn"
          title={productFilter === 'all' ? 'Select a product first' : 'Scan for quality issues'}
          aria-label="Scan backlog for quality issues"
        >
          {scanning ? (
            <><Loader2 size={14} className="animate-spin" /> Scanning...</>
          ) : (
            <><Scan size={14} /> Scan for Issues</>
          )}
        </button>
        )}
        
        {/* Quality Issues Badge */}
        {Object.keys(qualityIssues).length > 0 && (
          <button
            onClick={() => setShowScanModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full"
          >
            <AlertTriangle size={14} />
            {Object.keys(qualityIssues).length} Issues
          </button>
        )}
        
        {/* View Mode Toggle */}
        <div className="flex items-center border border-[#e3e3e3] dark:border-white/10 rounded-none overflow-hidden">
          <button
            onClick={() => setViewMode('hierarchy')}
            className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${viewMode === 'hierarchy' ? 'bg-[#98139C] text-white' : 'bg-white dark:bg-white/5 text-[#30302F] dark:text-white'}`}
            title="Hierarchy View"
            aria-label="Switch to hierarchy view"
          >
            <Layers size={14} aria-hidden="true" />
            Hierarchy
          </button>
          <button
            onClick={() => setViewMode('flat')}
            className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${viewMode === 'flat' ? 'bg-[#98139C] text-white' : 'bg-white dark:bg-white/5 text-[#30302F] dark:text-white'}`}
            title="Flat View"
            aria-label="Switch to flat view"
          >
            <List size={14} aria-hidden="true" />
            Flat
          </button>
          <button
            onClick={() => setViewMode('tasks')}
            className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${viewMode === 'tasks' ? 'bg-[#98139C] text-white' : 'bg-white dark:bg-white/5 text-[#30302F] dark:text-white'}`}
            title="ADO Tasks & Bugs"
            aria-label="Switch to ADO tasks view"
          >
            <CheckCircle size={14} aria-hidden="true" />
            Tasks
          </button>
        </div>
      </div>

      {/* State Filter Pills - hidden in tasks view */}
      {viewMode !== 'tasks' && <div className="flex gap-1.5 flex-wrap mb-6">
        {states.map((s) => (
          <button 
            key={s} 
            data-testid={`state-filter-${s}`} 
            onClick={() => setStateFilter(s)} 
            className={`text-xs px-3 py-1.5 rounded-full font-bold transition-colors ${
              stateFilter === s 
                ? 'bg-[#98139C] text-white' 
                : 'border border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white hover:border-[#98139C] hover:text-[#98139C]'
            }`}
          >
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>}

      {/* Expand/Collapse controls for hierarchy view */}
      {viewMode === 'hierarchy' && (
        <div className="flex gap-2 mb-4">
          <button 
            onClick={expandAll}
            className="text-xs px-3 py-1 border border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white hover:border-[#98139C] hover:text-[#98139C]"
          >
            Expand All
          </button>
          <button 
            onClick={collapseAll}
            className="text-xs px-3 py-1 border border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white hover:border-[#98139C] hover:text-[#98139C]"
          >
            Collapse All
          </button>
        </div>
      )}

      {viewMode !== 'tasks' && loading ? (
        <div className="text-center py-12 text-[#30302F] dark:text-white">Loading...</div>
      ) : viewMode === 'tasks' ? null : hierarchy.length === 0 && filteredStories.length === 0 ? (
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 py-16 text-center">
          <p className="text-[#30302F] dark:text-white">No items found</p>
        </div>
      ) : viewMode === 'hierarchy' ? (
        /* Hierarchy View */
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-lg overflow-hidden" data-testid="backlog-hierarchy">
          <div className="overflow-x-auto">
            <div className="min-w-[900px]">
              {/* Header */}
              <div className="flex items-center border-b border-[#e3e3e3] dark:border-white/10 bg-[#faf8fb] dark:bg-white/5 text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">
                <div className="w-6 flex-shrink-0 ml-4" />
                <div className="flex-1 py-3 pr-4 min-w-[200px]">Title</div>
                <div className="w-24 py-3 px-2">Type</div>
                <div className="w-32 py-3 px-2 hidden xl:block">Product</div>
                <div className="w-28 py-3 px-2">State</div>
                <div className="w-20 py-3 px-2">Priority</div>
                <div className="w-20 py-3 px-2 hidden 2xl:block">RICE</div>
                <div className="w-24 py-3 px-2 hidden 2xl:block">Readiness</div>
                <div className="w-24 py-3 px-2">ADO</div>
                <div className="w-24 py-3 px-2 hidden lg:block">Updated</div>
                <div className="w-20 py-3 px-2 text-center">Actions</div>
              </div>

              {/* Hierarchy rows wrapped in DndContext for drag-and-drop
                  reordering. Each parent renders its own SortableContext
                  so sibling drags stay scoped (drag-end also enforces this). */}
              <div className="overflow-y-auto max-h-[calc(100vh-400px)]">
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={hierarchy.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {hierarchy.map(item => renderRow(item, 0, item.type, null))}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Flat View */
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 overflow-hidden" data-testid="backlog-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Backlog items">
              <thead>
                <tr className="border-b border-[#e3e3e3] dark:border-white/10 bg-[#faf8fb] dark:bg-white/5">
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">Title</th>
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">Type</th>
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">Product</th>
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">State</th>
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">Priority</th>
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">RICE</th>
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">Readiness</th>
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">ADO</th>
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">Updated</th>
                  <th scope="col" className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.9px] text-[#30302F] dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map(item => renderFlatRow(item))}
                {(typeFilter === 'all' || typeFilter === 'user-story') &&
                  filteredStories.map(story => renderFlatRow(story))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* ADO Tasks View */}
      {viewMode === 'tasks' && (
        <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10">
          <div className="px-6 py-3 border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-[#30302F] dark:text-white">ADO Tasks & Bugs</h3>
              <span className="text-[11px] text-gray-400">{adoTasks.length} items synced via webhook</span>
            </div>
          </div>
          {loadingTasks ? (
            <div className="p-8 text-center text-[#30302F] dark:text-white">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading ADO tasks...
            </div>
          ) : adoTasks.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-[#30302F] dark:text-white font-medium">No tasks synced yet</p>
              <p className="text-sm text-gray-400 mt-1">Tasks and bugs created in ADO will appear here automatically via webhook.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#30302F] dark:text-white border-b border-[#e3e3e3] dark:border-white/10">
                  <th className="px-6 py-2">Title</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Assignee</th>
                  <th className="px-3 py-2">Priority</th>
                  <th className="px-3 py-2">Parent Story</th>
                  <th className="px-3 py-2">ADO</th>
                </tr>
              </thead>
              <tbody>
                {adoTasks.map((task) => (
                  <tr key={task.id || task.ado_id} className="border-b border-[#e3e3e3] dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5">
                    <td className="px-6 py-2.5">
                      <span className="text-[#30302F] dark:text-white">{task.title}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge className={`text-[10px] rounded-none ${task.type === 'Bug' ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'}`}>
                        {task.type}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge className="text-[10px] rounded-none bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300">
                        {task.state}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-[#30302F] dark:text-white">{task.assigned_to || '—'}</td>
                    <td className="px-3 py-2.5 text-xs">{task.priority ? `P${task.priority}` : '—'}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-400">{task.parent_id ? `#${task.parent_id}` : '—'}</td>
                    <td className="px-3 py-2.5">
                      {task.ado_url && (
                        <a href={task.ado_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#98139C] hover:underline flex items-center gap-0.5">
                          <ExternalLink size={10} /> #{task.ado_id}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Quality Scout Results Modal */}
      <Dialog open={showScanModal} onOpenChange={(open) => {
        setShowScanModal(open);
        if (!open && scanPollRef.current) {
          clearInterval(scanPollRef.current);
          scanPollRef.current = null;
          activeScanIdRef.current = null;
          setScanning(false);
          setScanProgress(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan size={20} className="text-[#98139C]" />
              Quality Scan Results
            </DialogTitle>
          </DialogHeader>

          {/* Progress indicator while scanning */}
          {scanning && scanProgress && (
            <div className="p-6 text-center">
              <Loader2 size={32} className="animate-spin mx-auto mb-3 text-[#98139C]" />
              <p className="font-medium text-[#30302F] dark:text-white mb-1">
                Scanning items for quality issues...
              </p>
              <p className="text-sm text-[#30302F]/70 dark:text-white/70">
                {scanProgress.processed_count} / {scanProgress.total_items} items analyzed
              </p>
              <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2 mt-3 max-w-xs mx-auto">
                <div
                  className="bg-[#98139C] h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${scanProgress.total_items > 0
                      ? Math.round((scanProgress.processed_count / scanProgress.total_items) * 100)
                      : 0}%`
                  }}
                />
              </div>
            </div>
          )}

          {scanResults && !scanning && (
            <div className="flex-1 overflow-y-auto">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4 p-4 bg-[#faf8fb] dark:bg-white/5 rounded-lg mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#30302F] dark:text-white">{scanResults.summary?.total_scanned || 0}</div>
                  <div className="text-xs text-[#30302F]/70 dark:text-white/70">Items Scanned</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{scanResults.summary?.critical_gaps || 0}</div>
                  <div className="text-xs text-[#30302F]/70 dark:text-white/70">Critical Gaps</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-600">{scanResults.summary?.needs_work || 0}</div>
                  <div className="text-xs text-[#30302F]/70 dark:text-white/70">Needs Work</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{scanResults.summary?.complete || 0}</div>
                  <div className="text-xs text-[#30302F]/70 dark:text-white/70">Complete</div>
                </div>
              </div>
              
              {/* Issues List */}
              <div className="space-y-3">
                {scanResults.issues?.filter(i => i.status !== 'complete').map((issue, idx) => (
                  <div key={idx} className="border border-[#e3e3e3] dark:border-white/10 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {issue.status === 'critical_gaps' ? (
                            <XCircle size={16} className="text-red-500" />
                          ) : (
                            <AlertTriangle size={16} className="text-amber-500" />
                          )}
                          <span className="font-medium text-[#30302F] dark:text-white">{issue.item_title}</span>
                          <Badge className={`text-[10px] ${issue.status === 'critical_gaps' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                            {issue.item_type}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[#30302F]/70 dark:text-white/70">
                          <span>Quality Score: <strong className={issue.quality_score < 50 ? 'text-red-500' : 'text-amber-500'}>{issue.quality_score}%</strong></span>
                          <span>{issue.missing_fields?.length || 0} missing fields</span>
                          <span>{issue.proposed_improvements?.length || 0} improvements suggested</span>
                        </div>
                      </div>
                      
                      {canAi && issue.proposed_improvements?.length > 0 && (
                        <button
                          onClick={() => applyAllImprovements(issue)}
                          disabled={applyingFix === issue.item_id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] disabled:opacity-50"
                        >
                          {applyingFix === issue.item_id ? (
                            <><Loader2 size={12} className="animate-spin" /> Applying...</>
                          ) : (
                            <><Wand2 size={12} /> Apply All Fixes</>
                          )}
                        </button>
                      )}
                    </div>
                    
                    {/* Missing Fields */}
                    {issue.missing_fields?.length > 0 && (
                      <div className="mb-3">
                        <div className="text-[11px] uppercase tracking-wide font-bold text-[#30302F]/70 dark:text-white/70 mb-1.5">Missing Fields</div>
                        <div className="flex flex-wrap gap-1.5">
                          {issue.missing_fields.map((field, fidx) => (
                            <span 
                              key={fidx} 
                              className={`px-2 py-0.5 text-[11px] rounded ${
                                field.severity === 'critical' 
                                  ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' 
                                  : field.severity === 'important'
                                    ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                                    : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-white/70'
                              }`}
                            >
                              {field.field}: {field.issue}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Proposed Improvements */}
                    {issue.proposed_improvements?.length > 0 && (
                      <div>
                        <div className="text-[11px] uppercase tracking-wide font-bold text-[#30302F]/70 dark:text-white/70 mb-1.5">Proposed Improvements</div>
                        <div className="space-y-2">
                          {issue.proposed_improvements.slice(0, 3).map((imp, iidx) => (
                            <div key={iidx} className="flex items-start gap-2 text-xs bg-green-50 dark:bg-green-900/10 p-2 rounded">
                              <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span className="font-medium text-green-700 dark:text-green-400">{imp.field}:</span>
                                <span className="text-[#30302F]/80 dark:text-white/80 ml-1 break-words">{imp.proposed?.slice(0, 150)}{imp.proposed?.length > 150 ? '...' : ''}</span>
                              </div>
                            </div>
                          ))}
                          {issue.proposed_improvements.length > 3 && (
                            <div className="text-xs text-[#30302F]/70 dark:text-white/70">
                              +{issue.proposed_improvements.length - 3} more improvements
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Summary */}
                    {issue.summary && (
                      <div className="mt-3 text-xs text-[#30302F]/80 dark:text-white/80 italic border-t border-[#e3e3e3] dark:border-white/10 pt-2">
                        {issue.summary}
                      </div>
                    )}
                  </div>
                ))}
                
                {scanResults.issues?.filter(i => i.status !== 'complete').length === 0 && (
                  <div className="text-center py-8 text-green-600">
                    <CheckCircle size={48} className="mx-auto mb-2" />
                    <p className="font-medium">All items look good!</p>
                    <p className="text-sm text-[#30302F]/70 dark:text-white/70">No quality issues found in the scanned items.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
